import type {
  PredefinedSkillDef,
  PredefinedSkillsManifest,
} from '@/versions/v1/shared/admin-seed';

export const SLUG_PATTERN = /^[a-z][a-z0-9-]{0,63}$/;
export const PREDEFINED_SKILLS_MAX_COUNT = 32;
export const PREDEFINED_SKILL_BODY_MAX_BYTES = 48 * 1024;

export const DEFAULT_BODY = `# Skill body

Describe, in markdown, what the agent should do when this skill applies.

- When to use it
- Inputs the agent needs
- Steps to follow
`;

export function bodyBytes(value: string): number {
  return new TextEncoder().encode(value).length;
}

export function emptySkillDef(): PredefinedSkillDef {
  return {
    slug: '',
    name: '',
    description: '',
    body: DEFAULT_BODY,
    enabled: true,
  };
}

export type SkillCreateValues = {
  slug: string;
  name?: string;
  description: string;
  body: string;
};

export function toFormValues(def: PredefinedSkillDef): SkillCreateValues {
  return {
    slug: def.slug,
    name: def.name ?? '',
    description: def.description,
    body: def.body,
  };
}

export function fromFormValues(
  values: SkillCreateValues,
  enabled: boolean,
): PredefinedSkillDef | null {
  const trimmedSlug = values.slug.trim();
  if (!SLUG_PATTERN.test(trimmedSlug)) return null;
  if (!values.description.trim() || !values.body.trim()) return null;
  if (bodyBytes(values.body) > PREDEFINED_SKILL_BODY_MAX_BYTES) return null;

  return {
    slug: trimmedSlug,
    description: values.description.trim(),
    body: values.body,
    enabled,
    ...(values.name?.trim() ? { name: values.name.trim() } : {}),
  };
}

export function countEnabled(manifest: PredefinedSkillsManifest): number {
  return manifest.skills.filter((skill) => skill.enabled).length;
}

export function toggleSkillEnabled(
  manifest: PredefinedSkillsManifest,
  slug: string,
): PredefinedSkillsManifest {
  return {
    skills: manifest.skills.map((skill) =>
      skill.slug === slug ? { ...skill, enabled: !skill.enabled } : skill,
    ),
  };
}

export function removeSkill(
  manifest: PredefinedSkillsManifest,
  slug: string,
): PredefinedSkillsManifest {
  return {
    skills: manifest.skills.filter((skill) => skill.slug !== slug),
  };
}

export function validateModalDraft(
  draft: PredefinedSkillDef | null,
  mode: 'add' | 'edit' | null,
): string | null {
  if (!draft) return 'Invalid form.';
  const trimmedSlug = draft.slug.trim();
  if (mode === 'add') {
    if (!trimmedSlug) return 'Slug is required.';
    if (!SLUG_PATTERN.test(trimmedSlug)) {
      return 'Use kebab-case starting with a letter.';
    }
  }
  if (!draft.description.trim()) return 'Description is required.';
  if (!draft.body.trim()) return 'Content is required.';
  if (bodyBytes(draft.body) > PREDEFINED_SKILL_BODY_MAX_BYTES) {
    return 'Content exceeds the size limit.';
  }
  return null;
}
